import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Calendar, Sun, Moon, Check, X, Sparkles, Droplets, 
  Zap, FlaskConical, AlertTriangle, Save, RotateCcw
} from "lucide-react";

const DAYS = [
  { key: "mon", label: "Mon", fullLabel: "Monday" },
  { key: "tue", label: "Tue", fullLabel: "Tuesday" },
  { key: "wed", label: "Wed", fullLabel: "Wednesday" },
  { key: "thu", label: "Thu", fullLabel: "Thursday" },
  { key: "fri", label: "Fri", fullLabel: "Friday" },
  { key: "sat", label: "Sat", fullLabel: "Saturday" },
  { key: "sun", label: "Sun", fullLabel: "Sunday" }
];

const ACTIVE_TYPES = [
  { 
    key: "retinol", 
    label: "Retinol", 
    color: "bg-purple-500", 
    lightColor: "bg-purple-100 text-purple-700 border-purple-300",
    icon: Sparkles,
    description: "Vitamin A derivative for anti-aging",
    recommendation: "Use 2-3x per week at night. Avoid mixing with AHAs/BHAs."
  },
  { 
    key: "aha", 
    label: "AHA", 
    color: "bg-pink-500", 
    lightColor: "bg-pink-100 text-pink-700 border-pink-300",
    icon: Droplets,
    description: "Glycolic, Lactic, Mandelic acids",
    recommendation: "Use 2-3x per week. Don't use same night as retinol."
  },
  { 
    key: "bha", 
    label: "BHA", 
    color: "bg-orange-500", 
    lightColor: "bg-orange-100 text-orange-700 border-orange-300",
    icon: Zap,
    description: "Salicylic acid for pores",
    recommendation: "Use 2-3x per week. Safe to layer with most actives."
  },
  { 
    key: "vitamin_c", 
    label: "Vitamin C", 
    color: "bg-amber-500", 
    lightColor: "bg-amber-100 text-amber-700 border-amber-300",
    icon: Sun,
    description: "Antioxidant brightening serum",
    recommendation: "Use daily in AM. Pairs well with SPF."
  }
];

export default function WeeklySchedule({ activeProducts = [] }) {
  const { token } = useAuth();
  const [schedule, setSchedule] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchSchedule();
  }, []);

  const fetchSchedule = async () => {
    try {
      const response = await axios.get(`${API}/auth/active-schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedule(response.data || {});
    } catch (error) {
      console.error("Failed to fetch schedule:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDay = (activeType, day) => {
    setSchedule(prev => {
      const currentDays = prev[activeType] || [];
      const newDays = currentDays.includes(day)
        ? currentDays.filter(d => d !== day)
        : [...currentDays, day];
      
      return { ...prev, [activeType]: newDays };
    });
    setHasChanges(true);
  };

  const saveSchedule = async () => {
    setSaving(true);
    try {
      // Save each active type's schedule
      for (const activeType of Object.keys(schedule)) {
        await axios.put(`${API}/auth/active-schedule`, {
          active_type: activeType,
          days: schedule[activeType] || []
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      toast.success("Weekly schedule saved!");
      setHasChanges(false);
    } catch (error) {
      toast.error("Failed to save schedule");
    } finally {
      setSaving(false);
    }
  };

  const resetSchedule = () => {
    setSchedule({});
    setHasChanges(true);
  };

  // Get products for each active type
  const getProductsForType = (activeType) => {
    return activeProducts.filter(p => p.active_type === activeType);
  };

  // Check for conflicts (e.g., retinol + AHA on same night)
  const getConflicts = (day) => {
    const conflicts = [];
    const daySchedule = ACTIVE_TYPES.filter(type => 
      (schedule[type.key] || []).includes(day)
    ).map(t => t.key);
    
    // Retinol + AHA conflict
    if (daySchedule.includes("retinol") && daySchedule.includes("aha")) {
      conflicts.push("Retinol + AHA can be irritating together");
    }
    // Retinol + BHA conflict (less severe)
    if (daySchedule.includes("retinol") && daySchedule.includes("bha")) {
      conflicts.push("Retinol + BHA may cause dryness");
    }
    
    return conflicts;
  };

  if (loading) {
    return (
      <Card className="card-product rounded-3xl">
        <CardContent className="p-8 text-center">
          <div className="animate-pulse">Loading schedule...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-product rounded-3xl" data-testid="weekly-schedule">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Weekly Active Schedule
            </CardTitle>
            <CardDescription>
              Plan when to use your active ingredients to avoid over-exfoliation
            </CardDescription>
          </div>
          <div className="flex gap-2">
            {hasChanges && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetSchedule}
                  data-testid="reset-schedule-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Reset
                </Button>
                <Button
                  size="sm"
                  onClick={saveSchedule}
                  disabled={saving}
                  data-testid="save-schedule-btn"
                >
                  <Save className="w-4 h-4 mr-1" />
                  {saving ? "Saving..." : "Save"}
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Legend */}
        <div className="flex flex-wrap gap-2 mb-6">
          {ACTIVE_TYPES.map(type => {
            const products = getProductsForType(type.key);
            const TypeIcon = type.icon;
            return (
              <TooltipProvider key={type.key}>
                <Tooltip>
                  <TooltipTrigger>
                    <Badge 
                      variant="outline" 
                      className={`${type.lightColor} cursor-help`}
                    >
                      <TypeIcon className="w-3 h-3 mr-1" />
                      {type.label}
                      {products.length > 0 && (
                        <span className="ml-1 opacity-70">({products.length})</span>
                      )}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="font-medium">{type.label}</p>
                    <p className="text-xs text-muted-foreground mb-1">{type.description}</p>
                    <p className="text-xs text-primary">{type.recommendation}</p>
                    {products.length > 0 && (
                      <div className="mt-2 pt-2 border-t">
                        <p className="text-xs font-medium">Your products:</p>
                        {products.slice(0, 3).map(p => (
                          <p key={p.id} className="text-xs text-muted-foreground">• {p.name}</p>
                        ))}
                      </div>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>

        {/* Schedule Grid */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="p-2 text-left text-sm font-medium text-muted-foreground w-24">Active</th>
                {DAYS.map(day => (
                  <th key={day.key} className="p-2 text-center text-sm font-medium">
                    <span className="hidden sm:inline">{day.label}</span>
                    <span className="sm:hidden">{day.label.charAt(0)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ACTIVE_TYPES.map(type => {
                const TypeIcon = type.icon;
                const products = getProductsForType(type.key);
                const isDisabled = products.length === 0;
                
                return (
                  <tr key={type.key} className={`border-t ${isDisabled ? 'opacity-40' : ''}`}>
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${type.color}`} />
                        <span className="text-sm font-medium">{type.label}</span>
                      </div>
                    </td>
                    {DAYS.map(day => {
                      const isActive = (schedule[type.key] || []).includes(day.key);
                      const conflicts = getConflicts(day.key);
                      const hasConflict = isActive && conflicts.length > 0;
                      
                      return (
                        <td key={day.key} className="p-1 text-center">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => !isDisabled && toggleDay(type.key, day.key)}
                                  disabled={isDisabled}
                                  className={`
                                    w-10 h-10 rounded-lg border-2 transition-all
                                    flex items-center justify-center
                                    ${isActive 
                                      ? `${type.color} text-white border-transparent shadow-md` 
                                      : 'border-border hover:border-primary/50 bg-white/50'
                                    }
                                    ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-105'}
                                    ${hasConflict ? 'ring-2 ring-amber-400 ring-offset-1' : ''}
                                  `}
                                  data-testid={`schedule-${type.key}-${day.key}`}
                                >
                                  {isActive ? (
                                    <Check className="w-5 h-5" />
                                  ) : (
                                    <span className="w-5 h-5" />
                                  )}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {isDisabled ? (
                                  <p>No {type.label} products in your library</p>
                                ) : (
                                  <>
                                    <p>{isActive ? 'Remove from' : 'Add to'} {day.fullLabel}</p>
                                    {hasConflict && (
                                      <div className="mt-1 text-amber-500 text-xs">
                                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                                        {conflicts[0]}
                                      </div>
                                    )}
                                  </>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Conflict Warnings */}
        {DAYS.some(day => getConflicts(day.key).length > 0) && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Schedule Conflicts Detected</p>
                <p className="text-xs text-amber-600 mt-1">
                  Some combinations may cause irritation for Dry + Aging skin. 
                  Consider alternating these actives on different nights.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Quick Tips */}
        <div className="mt-4 p-3 bg-primary/5 rounded-xl">
          <p className="text-sm font-medium text-primary mb-2">Tips for Dry + Aging Skin:</p>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>• Start with Retinol 2x/week, gradually increase to 3-4x</li>
            <li>• Use AHAs on alternate nights from Retinol</li>
            <li>• Vitamin C works best in the morning with SPF</li>
            <li>• Always follow with a rich moisturizer</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
